<template>
  <DataGridRow>
    <DataGridCell>
      <div class="item-name">
        <div class="item-title">{{ props.item.name }}</div>
        <div v-if="props.item.customUrl" class="item-detail">Custom URL: {{ props.item.customUrl }}</div>
      </div>
    </DataGridCell>
    <DataGridCell>
      <ContentTypeBadge :content-type="props.item.contentType || 'video'" />
    </DataGridCell>
    <DataGridCell align="center">
      <div class="status-container">
        <FormKit
          type="toggle"
          :model-value="props.item.status === REQUEST_CONTENT_STATUS.ACTIVE && !(props.item.status === REQUEST_CONTENT_STATUS.EXPIRED)"
          :disabled="isUpdatingStatus || isOrgUserViewOnly"
          @update:model-value="toggleStatus(props.item)"
          outer-class="!mb-0"
          data-testid="status-toggle"
        />
      </div>
    </DataGridCell>
    <DataGridCell>
      <a v-if="props.item.collectionName" :href="getCollectionUrl(props.item.collectionId)" class="collection-link" target="_blank" rel="noopener noreferrer">
        {{ props.item.collectionName }} ({{ props.item.items.length }})
      </a>
      <span v-else class="no-collection">No collection</span>
    </DataGridCell>
    <DataGridCell>
      <div class="custom-id">
        <span v-if="props.item.customId" class="custom-id-value">{{ props.item.customId }}</span>
        <span v-else class="no-custom-id">—</span>
      </div>
    </DataGridCell>
    <DataGridCell>
      <div class="date-text">{{ formatDate(props.item.createdAt) }}</div>
    </DataGridCell>
    <DataGridCell>
      <div class="expires-text">
        <span v-if="isExpired" class="expired-text">Expired</span>
        <span v-else-if="props.item.expiresAt" class="expires-value">{{ formatDate(props.item.expiresAt) }}</span>
        <span v-else class="no-expires">Never</span>
      </div>
    </DataGridCell>
    <DataGridCell align="right">
      <div class="action-buttons">
        <Button
          v-if="props.item.requestLink"
          type="link"
          variant="gray"
          icon="general.externalLink"
          tooltip="Open in new tab"
          @click.prevent.stop="openInNewTab(props.item.requestLink)"
          data-testid="open-link-button"
        />
        <Button
          v-if="props.item.requestLink"
          type="link"
          variant="gray"
          icon="general.copy"
          tooltip="Copy link"
          @click.stop.prevent="copyLink(props.item.requestLink)"
          data-testid="copy-link-button"
        />
        <Button
          v-if="!isOrgUserViewOnly"
          type="link"
          variant="gray"
          icon="general.edited"
          tooltip="Edit"
          @click.prevent.stop="handleEditClick"
          data-testid="edit-request-content-button"
        />
        <Button
          v-if="!isOrgUserViewOnly"
          type="link"
          variant="red"
          icon="general.trash"
          tooltip="Delete"
          @click.prevent.stop="handleDeleteClick"
          data-testid="delete-request-content-button"
        />
      </div>
    </DataGridCell>
  </DataGridRow>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { DataGridRow, DataGridCell } from '@/storybook/datagrid';
import { Button } from '@/storybook';
import type { RequestContent } from '@/store/useRequestContentStore';
import ContentTypeBadge from '../shared/ContentTypeBadge.vue';
import { useRequestContentFormatting } from '../../composables/useRequestContentFormatting';
import { useRequestContentStore } from '@/store/useRequestContentStore';
import { useUserStore } from '@/store/useUserStore';
import { useToast } from '@/composables/useToast';
import { REQUEST_CONTENT_STATUS } from '../../constants';

interface Props {
  item: RequestContent;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  edit: [item: RequestContent];
  delete: [item: RequestContent];
}>();

const { formatDate, copyLink } = useRequestContentFormatting();
const requestContentStore = useRequestContentStore();
const userStore = useUserStore();
const { isOrgUserViewOnly } = storeToRefs(userStore);
const { success, error } = useToast();

const toggleStatus = async (item: RequestContent) => {
  const result = await requestContentStore.toggleStatus(item);
  if (result.success) {
    success(result.message);
  } else {
    error(result.message || 'Failed to update request link status. Please try again.');
  }
};

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const getCollectionUrl = (collectionId: string) => {
  return `/collections/${collectionId}`;
};

const isUpdatingStatus = computed(() => requestContentStore.isItemLoading(props.item.id));

// Check if the item has expired based on expiresAt date, regardless of status
const isExpired = computed(() => {
  if (!props.item.expiresAt) {
    return false;
  }
  return new Date() > new Date(props.item.expiresAt);
});

const handleEditClick = () => {
  emit('edit', props.item);
};

const handleDeleteClick = () => {
  emit('delete', props.item);
};
</script>

<style scoped>
.item-name {
  @apply space-y-1;
}

.item-title {
  @apply font-medium text-zinc-900 dark:text-zinc-100;
}

.item-detail {
  @apply text-xs text-zinc-500 dark:text-zinc-400;
}

.status-container {
  @apply flex items-center justify-center;
}

.collection-link {
  @apply text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700 dark:hover:text-blue-300 hover:underline transition-colors cursor-pointer;
}

.custom-id-value {
  @apply text-zinc-900 dark:text-zinc-100 font-mono text-xs;
}

.expires-value,
.items-count {
  @apply text-zinc-600 dark:text-zinc-300;
}

.date-text {
  @apply text-zinc-500 dark:text-zinc-400;
}

.no-collection,
.no-custom-id,
.no-expires {
  @apply text-zinc-400 dark:text-zinc-500 italic;
}

.expired-text {
  @apply text-red-600 dark:text-red-400 font-medium;
}

.action-buttons {
  @apply flex justify-end gap-1;
}
</style>
